import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, AlertCircle, Send, Mail, MonitorUp, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("verifying");
  const [tvUsername, setTvUsername] = useState("");
  const [tvState, setTvState] = useState("idle");
  const [tvError, setTvError] = useState("");

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
      } catch {
        // keep polling briefly
      }
      attempts += 1;
      if (attempts < 12) {
        setTimeout(poll, 2000);
      } else {
        setState("pending");
      }
    };
    poll();
  }, [sessionId]);

  const submitUsername = async (e) => {
    e.preventDefault();
    setTvState("sending");
    setTvError("");
    try {
      await axios.post(`${API}/access/submit`, { session_id: sessionId, tv_username: tvUsername });
      setTvState("done");
    } catch (err) {
      setTvError(err.response?.data?.detail || "Something went wrong — please try again.");
      setTvState("idle");
    }
  };

  return (
    <div className="min-h-screen bg-ink flex items-center justify-center px-5 py-16" data-testid="payment-success-page">
      <div className="max-w-lg w-full glass-panel gold-ring chart-frame border border-line p-8 sm:p-10">
        {state === "verifying" && (
          <div className="text-center">
            <Loader2 size={34} className="animate-spin text-gold mx-auto" />
            <h1 className="mt-6 font-display font-bold text-2xl text-slate-50">Confirming your payment…</h1>
            <p className="mt-3 text-sm text-slate-400">Hold tight — we're verifying your access with Stripe.</p>
          </div>
        )}

        {state === "paid" && (
          <div data-testid="payment-confirmed-block">
            <div className="text-center">
              <CheckCircle2 size={38} className="text-gold mx-auto" data-testid="payment-confirmed-icon" />
              <h1 className="mt-5 font-display font-extrabold text-3xl text-slate-50" data-testid="payment-confirmed-title">
                Welcome to <span className="text-gold-gradient">Atlas.</span>
              </h1>
              <p className="mt-3 text-sm text-slate-400">
                Your payment is confirmed — ATLAS Trading Indicator, lifetime access. Three quick steps and you're in:
              </p>
            </div>

            <div className="mt-8 space-y-6">
              <div className="flex gap-4 items-start" data-testid="success-step-1">
                <span className="mt-0.5 w-9 h-9 shrink-0 rounded-sm border border-gold/30 bg-gold/5 flex items-center justify-center">
                  <Mail size={15} className="text-gold" />
                </span>
                <div>
                  <h3 className="font-display font-bold text-sm sm:text-base text-slate-100">01 — Check your email</h3>
                  <p className="mt-1 text-sm text-slate-400 leading-relaxed">
                    Your confirmation email contains your <span className="text-gold">exclusive Telegram invite link</span>.
                    Not in your inbox within a minute? Check spam/junk. A Stripe receipt is on its way too.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-start" data-testid="success-step-2">
                <span className="mt-0.5 w-9 h-9 shrink-0 rounded-sm border border-gold/30 bg-gold/5 flex items-center justify-center">
                  <MonitorUp size={15} className="text-gold" />
                </span>
                <div className="flex-1">
                  <h3 className="font-display font-bold text-sm sm:text-base text-slate-100">02 — Submit your TradingView username</h3>
                  {tvState === "done" ? (
                    <p className="mt-2 text-sm text-emerald-400 flex items-center gap-2" data-testid="tv-form-success">
                      <CheckCircle2 size={14} /> Username received. Access is granted manually — usually within 24 hours.
                    </p>
                  ) : (
                    <form onSubmit={submitUsername} className="mt-3" data-testid="tv-username-form">
                      <div className="flex gap-2">
                        <input
                          required
                          value={tvUsername}
                          onChange={(e) => setTvUsername(e.target.value)}
                          placeholder="Your TradingView username"
                          data-testid="tv-username-input"
                          className="flex-1 bg-ink/70 border border-line rounded-sm px-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-gold/60 transition-colors"
                        />
                        <button
                          type="submit"
                          disabled={tvState === "sending"}
                          data-testid="tv-username-submit"
                          className="inline-flex items-center gap-2 bg-gold text-ink font-semibold text-sm px-4 py-2.5 rounded-sm hover:bg-gold-bright transition-colors disabled:opacity-60"
                        >
                          {tvState === "sending" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                          Submit
                        </button>
                      </div>
                      {tvError && <p className="mt-2 text-xs text-red-400" data-testid="tv-form-error">{tvError}</p>}
                      <p className="mt-2 text-[11px] text-slate-500">
                        Indicator access is switched on for this username — typically within 24 hours, often much sooner.
                      </p>
                    </form>
                  )}
                </div>
              </div>

              <div className="flex gap-4 items-start" data-testid="success-step-3">
                <span className="mt-0.5 w-9 h-9 shrink-0 rounded-sm border border-gold/30 bg-gold/5 flex items-center justify-center">
                  <Users size={15} className="text-gold" />
                </span>
                <div>
                  <h3 className="font-display font-bold text-sm sm:text-base text-slate-100">03 — Join the community</h3>
                  <p className="mt-1 text-sm text-slate-400 leading-relaxed">
                    Exclusive room link is in your email. The free community is open right now:
                  </p>
                  <a href="https://t.me/addlist/O9NU4HFXS-cxYzNk" target="_blank" rel="noopener noreferrer" data-testid="payment-telegram-cta"
                    className="mt-3 inline-flex items-center gap-2 border border-gold/40 text-gold font-medium text-sm px-5 py-2.5 rounded-sm hover:bg-gold hover:text-ink transition-colors duration-300">
                    <Send size={14} /> Free Atlas Community
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {state === "pending" && (
          <div className="text-center">
            <AlertCircle size={34} className="text-gold mx-auto" />
            <h1 className="mt-6 font-display font-bold text-2xl text-slate-50">Payment processing</h1>
            <p className="mt-3 text-sm text-slate-400 leading-relaxed">
              Your payment is still being confirmed. If you completed checkout, your access will activate
              automatically — check your email, or message us in the Telegram community if anything looks wrong.
            </p>
          </div>
        )}

        {state === "error" && (
          <div className="text-center">
            <AlertCircle size={34} className="text-red-400 mx-auto" />
            <h1 className="mt-6 font-display font-bold text-2xl text-slate-50">Something's missing</h1>
            <p className="mt-3 text-sm text-slate-400">We couldn't find this checkout session. Please contact support via Telegram.</p>
          </div>
        )}

        <div className="text-center">
          <Link to="/" data-testid="payment-back-home-link"
            className="mt-9 inline-block text-xs font-mono tracking-[0.2em] text-slate-500 hover:text-gold transition-colors uppercase">
            ← Back to Atlas
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
